import { agentName } from "@workforceos/shared";
import { useNavigate, useParams } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../../api/client";
import { validatorLabel } from "../../lib/validatorLabel";
import { formatWhen } from "../../lib/when";

/**
 * Agent health — the agent handler's page.
 *
 * A failed run holds no business decision, so it is nobody's judgement fork
 * and it appeared in no queue and on no screen: the run happened, the row was
 * written, and the person whose job is to maintain the agents had nothing to
 * look at. This is that: what failed and on which validator, which schedules
 * could not do what they were asked, and which agents are not scheduled at
 * all.
 */

interface Failure {
  run_id: string;
  hand_key: string;
  hand_name: string;
  created_at: string;
  duration_ms: number;
  llm_model: string | null;
  failed_validators: string[];
}

interface Schedule {
  hand_key: string;
  hand_name: string;
  kind: string;
  enabled: boolean;
  next_run_at: string | null;
  last_run_at: string | null;
  last_error: string | null;
}

export interface RulesCache {
  hits: number;
  misses: number;
  entries: number;
  hit_rate: number | null;
  pack_version: string | null;
  last_invalidated: string | null;
}

/** One line: how often rules came from the cache, and which pack. */
export function rulesCacheLine(c: RulesCache): string {
  if (c.hit_rate === null) return "No rule lookups yet";
  const pct = `${Math.round(c.hit_rate * 100)}% of rule lookups served from cache`;
  return c.pack_version ? `${pct} · pack ${c.pack_version}` : pct;
}

interface Health {
  rules_cache?: RulesCache;
  failures: Failure[];
  schedules: Schedule[];
  unscheduled: string[];
  rejected_after_drafting: number;
}

function AgentHealthInner() {
  const { workspaceId = "" } = useParams();
  const navigate = useNavigate();
  const q = useQuery<Health, Error>({
    queryKey: ["agent-health", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => apiGet(`/api/workspaces/${workspaceId}/agent-health`),
  });

  const failures = q.data?.failures ?? [];
  const schedules = q.data?.schedules ?? [];
  const broken = schedules.filter((s) => s.last_error);

  return (
    <div className="main-inner" data-testid="agent-health">
      <div className="greet-row">
        <span className="greet">Runs and schedules</span>
      </div>
      <div className="subgreet">
        <div className="section-eyebrow">MAINTENANCE</div>
        <div className="lede" data-testid="health-lede">
          {q.isLoading
            ? "Checking the runs…"
            : failures.length === 0 && broken.length === 0
              ? "Nothing is broken. Every agent ran as asked."
              : `${failures.length} failed ${failures.length === 1 ? "run" : "runs"}${
                  broken.length > 0
                    ? ` and ${broken.length} ${broken.length === 1 ? "schedule" : "schedules"} that could not run`
                    : ""
                }.`}
        </div>
        {q.data && q.data.rejected_after_drafting > 0 ? (
          <div className="play-meta" style={{ marginTop: 6 }}>
            <span>
              {q.data.rejected_after_drafting} run
              {q.data.rejected_after_drafting === 1 ? "" : "s"} reached a person
              because the draft was rejected twice — worth reading if the same
              agent keeps appearing.
            </span>
          </div>
        ) : null}
      </div>

      {q.data?.rules_cache ? (
        <div className="play-grid">
          <div className="play-card" data-testid="rules-cache">
            <div className="play-title">Rules cache</div>
            <div className="play-meta">
              <span>{rulesCacheLine(q.data.rules_cache)}</span>
            </div>
          </div>
        </div>
      ) : null}

      <div className="section-h">
        <span className="ttl">Failed runs</span>
        <span className="meta">{failures.length} in the last 50 runs recorded</span>
      </div>
      <div className="play-grid">
        {failures.length === 0 ? (
          <div className="play-card" data-testid="failures-empty">
            <div className="play-title">No failures.</div>
            <div className="play-meta">
              <span>
                A run that stops at a rule is not a failure — that is the gate
                doing its job, and it is in My queue.
              </span>
            </div>
          </div>
        ) : (
          failures.map((f) => (
            <div
              key={f.run_id}
              className="play-card"
              data-testid={`failure-${f.run_id}`}
              onClick={() => navigate(`/workspaces/${workspaceId}/workforce/runs/${f.run_id}`)}
              style={{ cursor: "pointer" }}
            >
              <div className="play-head">
                <div>
                  <div className="play-arch">{f.hand_name}</div>
                  <div className="play-title">
                    {f.failed_validators.length > 0
                      ? validatorLabel(f.failed_validators[0]!)
                      : "The run did not complete."}
                  </div>
                </div>
                <div className="play-status">
                  <span className="pill red">failed</span>
                </div>
              </div>
              <div className="play-foot">
                <span>{formatWhen(f.created_at)}</span>
                <span>{f.llm_model ?? "no model called"}</span>
                <span className="v">Open the run →</span>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="section-h">
        <span className="ttl">Schedules</span>
        <span className="meta">{schedules.length} configured</span>
      </div>
      <div className="play-grid">
        {schedules.map((s) => (
          <div key={s.hand_key} className="play-card" data-testid={`schedule-${s.hand_key}`}>
            <div className="play-head">
              <div>
                <div className="play-arch">{s.hand_name}</div>
                <div className="play-title">
                  {s.kind === "cron" ? "On a schedule" : "On an event"}
                </div>
              </div>
              <div className="play-status">
                <span className={`pill ${s.last_error ? "red" : s.enabled ? "green" : "gray"}`}>
                  {s.last_error ? "last run failed" : s.enabled ? "enabled" : "off"}
                </span>
              </div>
            </div>
            <div className="play-meta">
              <span>
                Last run <span className="v">{s.last_run_at ? formatWhen(s.last_run_at) : "never"}</span>
              </span>
              {s.next_run_at ? (
                <span>
                  Next <span className="v">{formatWhen(s.next_run_at)}</span>
                </span>
              ) : null}
            </div>
            {s.last_error ? (
              <div className="play-meta" data-testid={`schedule-error-${s.hand_key}`}>
                <span className="pill red">{s.last_error}</span>
              </div>
            ) : null}
          </div>
        ))}
        {(q.data?.unscheduled ?? []).length > 0 ? (
          <div className="play-card" data-testid="unscheduled">
            <div className="play-title">Not scheduled yet</div>
            <div className="play-meta">
              <span>
                {q.data!.unscheduled.map((k) => agentName(k, "en")).join(", ")} — these will pick up a schedule
                on the next tick.
              </span>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function AgentHealth() {
  return (
    <ErrorBoundary
      fallbackRender={({ error }) => (
        <div className="main-inner" data-testid="agent-health-error">
          <div className="lede">Could not load agent health: {error.message}</div>
        </div>
      )}
    >
      <AgentHealthInner />
    </ErrorBoundary>
  );
}
